import { FC, HTMLAttributeAnchorTarget, PropsWithChildren, ElementType } from 'react';

import classnames from 'classnames';

import dataAttributes from '../../../../utils/attributes/attributes';
import { Box } from '../../../box/components/Box/Box';
import { Icon } from '../../../icon/components/Icon/Icon';
import { Typography } from '../../../typography/components/Typography/Typography';

const iconColors = [
   'bg-component-data-display-facet-background-color-segment-1 text-component-data-display-facet-icon-color-segment-1',
   'bg-component-data-display-facet-background-color-segment-2 text-component-data-display-facet-icon-color-segment-2',
   'bg-component-data-display-facet-background-color-segment-3 text-component-data-display-facet-icon-color-segment-3',
   'bg-component-data-display-facet-background-color-account text-component-data-display-facet-icon-color-account',
   'bg-component-data-display-facet-background-color-neutral text-component-data-display-facet-icon-color-neutral'
];

interface Props extends PropsWithChildren {
   icon?: string;
   size?: 'md' | 'lg';
   color?: number;
   trailingIcon?: string;
   href?: string;
   target?: HTMLAttributeAnchorTarget;
   title?: string;
   fullWidth?: boolean;
   active?: boolean;
   attributes?: Record<string, string>;
   linkComponent?: ElementType;
   onClick?: () => void;
}

export const NavItem: FC<Props> = ({ children, icon, color, trailingIcon, href, target, size = 'md', title, fullWidth = false, active, attributes = {}, linkComponent, onClick }) => {
   const LinkComponent = linkComponent ?? 'a';

   return (
      <div
         data-testid={`nav-item--${size}${fullWidth ? '--full-width' : ''}`}
         {...dataAttributes(attributes)}
         className={classnames(
            '!m-0 list-none transition-all',
            size === 'md' && 'rounded-component-data-display-nav-item-container-border-radius-small',
            size === 'lg' && 'rounded-component-data-display-nav-item-container-border-radius-large',
            active && 'bg-component-data-display-nav-item-state-layer-color-background-default-pressed',
            (href || onClick) &&
               'hover:bg-component-data-display-nav-item-container-color-background-default-hover active:bg-component-data-display-nav-item-container-color-background-default-pressed focus-within:bg-component-data-display-nav-item-container-color-background-default-focused cursor-pointer'
         )}
      >
         <LinkComponent
            data-testid="nav-item-anchor"
            href={href}
            target={target}
            title={title}
            onClick={onClick}
            className={classnames('gap-component-data-display-nav-item-container-spacing-gap p-component-data-display-nav-item-container-spacing-padding flex items-center', fullWidth && 'w-full', !icon && 'min-h-component-data-display-nav-item-container-sizing-min-height')}
         >
            <Box gap="sm" items="center" width={fullWidth ? '100%' : 'auto'}>
               {icon && (
                  <div
                     data-testid="nav-item-icon"
                     className={classnames(
                        'flex items-center justify-center',
                        color !== undefined && iconColors[color % iconColors.length],
                        size === 'md' && 'rounded-component-data-display-nav-item-facet-border-radius-small p-component-data-display-facet-container-spacing-padding-small',
                        size === 'lg' && 'rounded-component-data-display-nav-item-facet-border-radius-large p-component-data-display-facet-container-spacing-padding-large'
                     )}
                  >
                     <Icon icon={icon} color={color === undefined ? 'primary' : 'inherit'} size="body" />
                  </div>
               )}

               {children && (
                  <div className={classnames('text-component-data-display-nav-item-text-color-title', fullWidth && 'grow')}>
                     <Typography variant={size === 'md' ? 'body-highlight' : 'heading-4'} color="inherit">
                        <Box width="100%" direction="column" gap="3xs">
                           {children}
                        </Box>
                     </Typography>
                  </div>
               )}

               {trailingIcon && (
                  <div data-testid="nav-item-trailing-icon" className="text-component-data-display-search-item-icon-end-color-fill-enabled flex h-full items-center justify-center">
                     <Icon icon={trailingIcon} color="inherit" size="body" />
                  </div>
               )}
            </Box>
         </LinkComponent>
      </div>
   );
};
